import { useState } from 'react'
import { fbt } from 'fbtee'
import { Check } from 'lucide-react'
import { Button } from './ui/button'
import { Input } from './ui/input'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import { GROUP_COVER_PRESETS } from '../lib/groupCoverPresets'
import { cn } from '../lib/utils'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreate: (name: string, imageUrl?: string) => Promise<string>
  onCreated?: (groupId: string, name: string) => void
}

export function CreateGroupDialog({ open, onOpenChange, onCreate, onCreated }: Props) {
  const [draftName, setDraftName] = useState('')
  const [draftCover, setDraftCover] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  function reset() {
    setDraftName('')
    setDraftCover(null)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!draftName.trim()) return
    setSubmitting(true)
    const name = draftName.trim()
    const id = await onCreate(name, draftCover ?? undefined)
    setSubmitting(false)
    reset()
    onOpenChange(false)
    if (id) onCreated?.(id, name)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>
            <fbt desc="Dialog title for creating a new group">Create group</fbt>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            autoFocus
            placeholder={fbt('Group name', 'Placeholder text for the group name input')}
            value={draftName}
            onChange={e => setDraftName(e.target.value)}
          />
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              <fbt desc="Label above the cover image picker in the create group dialog">
                Choose a cover image
              </fbt>
            </p>
            <div className="grid grid-cols-4 gap-2">
              {GROUP_COVER_PRESETS.map((url, i) => {
                const selected = url === draftCover
                return (
                  <button
                    key={url}
                    type="button"
                    onClick={() => setDraftCover(selected ? null : url)}
                    aria-label={fbt(
                      'Cover ' + fbt.param('number', i + 1),
                      'Accessible label for a cover preset thumbnail in the create group dialog',
                    )}
                    className={cn(
                      'relative aspect-video rounded-md overflow-hidden border-2 transition-all cursor-pointer',
                      selected ? 'border-primary' : 'border-transparent hover:border-border',
                    )}
                  >
                    <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
                    {selected && (
                      <div className="absolute inset-0 bg-primary/30 flex items-center justify-center">
                        <Check className="size-4 text-on-image drop-shadow" />
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting || !draftName.trim()}>
              {submitting
                ? fbt('Creating…', 'Button label while a group is being created')
                : fbt('Create', 'Button label to submit the create group form')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
